/**
 * Data Sources step.
 *
 * Connects to a GeoServer or ArcGIS REST service, lists its layers, and adds
 * the chosen ones to the dashboard. Discovery goes through the shared
 * `discoverLayers` adapter, so the same URLs that work in the Map App builder
 * work here.
 *
 * Service URLs are always entered by the admin — nothing is hard-coded, so the
 * same build runs against dev, UAT, and production services.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  Database,
  FileUp,
  Folder,
  Layers as LayersIcon,
  Loader2,
  MapPin,
  Minus,
  Slash,
  Square,
  Trash2,
} from 'lucide-react';
import type { LayerGeometryType, ServerConnection } from '../types';
import {
  serverConnectionsService,
  type ServerConnectionBrowseResult,
  type ServerConnectionService,
} from '../services/serverConnectionsService';
import { createId } from '../services/storage';
import {
  LocalFileError,
  MAX_GDB_FILE_BYTES,
  MAX_LOCAL_FILE_BYTES,
  parseLocalFile,
  resolveLocalLayers,
  type ParsedImport,
} from '../layers/localFile';
import {
  discoverLayers,
  resolveGeometryType,
  type DiscoveredLayer,
} from './services/dashboardDataService';
import {
  candidateToSource,
  describeAppLayers,
  describeDataSource,
  removeDataSource,
  sourceExists,
} from './services/dashboardService';
import { moduleService } from '../services/moduleService';
import { useAuth } from '../auth/AuthContext';
import { invalidateFields } from './services/fieldCache';
import type {
  DashboardConfig,
  DashboardDataSource,
  DashboardServiceType,
} from './types/dashboardTypes';

interface Props {
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
  /** This dashboard, so it cannot offer itself as a source of layers. */
  moduleId?: string;
}

const GEOMETRY_ICONS: Record<LayerGeometryType, typeof MapPin> = {
  point: MapPin,
  line: Slash,
  polygon: Square,
  unknown: Minus,
};

export default function DashboardDataSources({ config, onChange, moduleId }: Props) {
  const { currentUser } = useAuth();

  /* Map applications this person may open, which is the only honest list to
     offer: the access boundary lives in the selector layer, so a dashboard
     cannot borrow layers from a map its author was never given. */
  const apps = useMemo(
    () =>
      moduleService
        .listModulesForUser(currentUser, moduleService.listAllModules())
        .filter((entry) => (entry.kind ?? 'map') === 'map' && entry.id !== moduleId),
    [currentUser, moduleId],
  );

  const [appId, setAppId] = useState('');
  const [appPicked, setAppPicked] = useState<Set<string>>(new Set());

  const chosenApp = apps.find((entry) => entry.id === appId) ?? null;
  const appLayers = useMemo(
    () => (chosenApp ? describeAppLayers(chosenApp) : []),
    [chosenApp],
  );
  const usableAppLayers = appLayers.filter((entry) => !entry.unusable);

  const [serviceType, setServiceType] = useState<DashboardServiceType>('geoserver');
  const [url, setUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState<DiscoveredLayer[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [connectedUrl, setConnectedUrl] = useState('');

  /* Saved connections (Settings › ArcGIS Servers / GeoServer) — the same
     "Add layers › Saved connection" picker the Map App builder offers, so a
     dashboard never has to have its own URL/credentials re-typed once a
     connection is saved once. See AddLayersDialog for the original. */
  const [urlSource, setUrlSource] = useState<'manual' | 'connection'>('manual');
  const [connections, setConnections] = useState<ServerConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  /* ArcGIS only: one folder's worth of the selected connection's services —
     null before "Connect" is pressed. */
  const [connectionBrowse, setConnectionBrowse] = useState<ServerConnectionBrowseResult | null>(null);
  const [connectionBrowseLoading, setConnectionBrowseLoading] = useState(false);
  const [connectionBrowseError, setConnectionBrowseError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setConnectionsLoading(true);
    Promise.all([
      serverConnectionsService.list('arcgis').catch(() => []),
      serverConnectionsService.list('geoserver').catch(() => []),
    ])
      .then(([arcgisConnections, geoserverConnections]) => {
        if (!cancelled) setConnections([...arcgisConnections, ...geoserverConnections]);
      })
      .finally(() => {
        if (!cancelled) setConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Runs discovery against an already-resolved (credential-bearing) URL,
   *  sharing the exact same result handling `connect` below uses for a
   *  manually-typed one — the picker below neither knows nor cares which. */
  const discoverFromUrl = async (resolvedUrl: string) => {
    setConnecting(true);
    setError(null);
    setAvailable([]);
    setSelected(new Set());
    try {
      const layers = await discoverLayers(serviceType, resolvedUrl);
      setAvailable(layers);
      setConnectedUrl(resolvedUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to reach this service');
    } finally {
      setConnecting(false);
    }
  };

  /** "Connect" for a saved connection — GeoServer resolves straight to
   *  discovery (one WMS endpoint already lists every layer); ArcGIS starts
   *  browsing the connection's services instead, since it may serve more
   *  than one. See AddLayersDialog's identical split for the full reasoning. */
  const connectSelectedConnection = async () => {
    if (!selectedConnectionId) return;
    if (serviceType === 'arcgis') {
      await browseConnection('');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      const { authenticatedBaseUrl } = await serverConnectionsService.resolve(selectedConnectionId);
      await discoverFromUrl(authenticatedBaseUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not connect to this server.');
    } finally {
      setConnecting(false);
    }
  };

  const browseConnection = async (path: string) => {
    if (!selectedConnectionId) return;
    setConnectionBrowseLoading(true);
    setConnectionBrowseError(null);
    try {
      const result = await serverConnectionsService.browse(selectedConnectionId, path);
      setConnectionBrowse(result);
    } catch (caught) {
      setConnectionBrowseError(caught instanceof Error ? caught.message : 'Could not browse this server.');
    } finally {
      setConnectionBrowseLoading(false);
    }
  };

  const selectConnectionService = async (service: ServerConnectionService) => {
    if (!selectedConnectionId) return;
    setConnecting(true);
    setError(null);
    try {
      const { authenticatedBaseUrl } = await serverConnectionsService.resolveService(
        selectedConnectionId,
        service,
      );
      setConnectionBrowse(null);
      await discoverFromUrl(authenticatedBaseUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not connect to this service.');
      setConnecting(false);
    }
  };

  const connect = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError(
        serviceType === 'geoserver'
          ? 'Enter a GeoServer WMS URL'
          : 'Enter an ArcGIS REST service URL',
      );
      return;
    }
    await discoverFromUrl(trimmed);
  };

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addSelected = () => {
    const additions: DashboardDataSource[] = [];

    for (const layer of available) {
      if (!selected.has(layer.id)) continue;

      // Adding the same service layer twice would produce two identical
      // entries in every picker; skip silently.
      const duplicate = config.dataSources.some(
        (source) =>
          source.serviceUrl === connectedUrl &&
          source.layerName === layer.name &&
          source.arcgisLayerId === layer.arcgisLayerId,
      );
      if (duplicate) continue;

      additions.push({
        id: createId('ds'),
        name: layer.title || layer.name,
        serviceType,
        serviceUrl: connectedUrl,
        layerName: layer.name,
        arcgisLayerId: layer.arcgisLayerId,
        geometryType: layer.geometryType,
        addedAt: new Date().toISOString(),
      });
    }

    if (additions.length === 0) return;

    const next = { ...config, dataSources: [...config.dataSources, ...additions] };
    onChange(next);
    setSelected(new Set());

    // GeoServer does not publish geometry type over WMS; resolve it in the
    // background so widget pickers and the map widget can label layers.
    for (const source of additions) {
      if (source.serviceType !== 'geoserver') continue;
      void resolveGeometryType(source).then((geometryType) => {
        if (geometryType === 'unknown') return;
        onChange({
          ...next,
          dataSources: next.dataSources.map((entry) =>
            entry.id === source.id ? { ...entry, geometryType } : entry,
          ),
        });
      });
    }
  };

  /** Adds the ticked layers of the chosen app. */
  const addFromApp = () => {
    const additions = usableAppLayers
      .filter((candidate) => appPicked.has(candidate.layerId))
      .filter((candidate) => !sourceExists(config, candidate))
      .map(candidateToSource);

    if (additions.length === 0) return;

    const next = { ...config, dataSources: [...config.dataSources, ...additions] };
    onChange(next);
    setAppPicked(new Set());

    /* Same background resolution the service path does: a map layer added
       before geometry capture existed carries none, and the widget pickers
       need it to label a source. */
    for (const source of additions) {
      if (source.serviceType !== 'geoserver' || source.geometryType !== 'unknown') continue;
      void resolveGeometryType(source).then((geometryType) => {
        if (geometryType === 'unknown') return;
        onChange({
          ...next,
          dataSources: next.dataSources.map((entry) =>
            entry.id === source.id ? { ...entry, geometryType } : entry,
          ),
        });
      });
    }
  };

  /* ── From this computer ───────────────────────────────────────────────
     The same importer the map builder uses, so every format it accepts —
     GeoJSON, KML, KMZ, zipped Shapefile, CSV/TSV, and a zipped File
     Geodatabase read on the server — becomes a dashboard source too. The
     features are COPIED into the dashboard; see DashboardDataSource.localData
     for why they have to be. */
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileParsing, setFileParsing] = useState(false);
  const [fileParsingLabel, setFileParsingLabel] = useState('Reading file…');
  const [fileImport, setFileImport] = useState<ParsedImport | null>(null);
  const [filePicked, setFilePicked] = useState<Set<number>>(new Set());
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileDragging, setFileDragging] = useState(false);
  const [fileAdding, setFileAdding] = useState<{ done: number; total: number } | null>(null);

  const resetFileImport = () => {
    setFileImport(null);
    setFilePicked(new Set());
    setFileError(null);
    setFileAdding(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleLocalFile = async (file: File | undefined) => {
    if (!file) return;
    // Releases the previous geodatabase upload, if the last file was one.
    fileImport?.release?.();

    setFileParsing(true);
    setFileParsingLabel(
      /\.zip$/i.test(file.name) && file.size > MAX_LOCAL_FILE_BYTES
        ? 'Uploading geodatabase…'
        : 'Reading file…',
    );
    setFileError(null);
    setFileImport(null);

    try {
      const result = await parseLocalFile(file);
      setFileImport(result);
      setFilePicked(
        new Set(result.layers.flatMap((layer, index) => (layer.unavailable ? [] : [index]))),
      );
    } catch (caught) {
      setFileError(
        caught instanceof LocalFileError
          ? caught.message
          : 'This file could not be read. Use GeoJSON, KML, a zipped shapefile, or a CSV with longitude/latitude columns.',
      );
    } finally {
      setFileParsing(false);
    }
  };

  /** Turns the ticked layers into data sources. */
  const addFromFile = async () => {
    if (!fileImport || filePicked.size === 0) return;
    const chosen = fileImport.layers.filter((_, index) => filePicked.has(index));

    setFileAdding({ done: 0, total: chosen.length });
    setFileError(null);
    try {
      /* Where a geodatabase's chosen feature classes are actually converted —
         until now only their names and counts had been read. */
      const loaded = await resolveLocalLayers(chosen, (done, total) =>
        setFileAdding({ done, total }),
      );

      const additions: DashboardDataSource[] = loaded.map((layer) => ({
        id: createId('ds'),
        name: layer.name,
        serviceType: 'local',
        // No service to address, and no type name to look up. A local source
        // is identified by its id alone.
        serviceUrl: '',
        layerName: layer.name,
        geometryType: layer.geometryType,
        localData: layer.data,
        originFile: fileImport.sourceName,
        addedAt: new Date().toISOString(),
      }));

      onChange({ ...config, dataSources: [...config.dataSources, ...additions] });
      fileImport.release?.();
      resetFileImport();
    } catch (caught) {
      setFileError(caught instanceof Error ? caught.message : 'Those layers could not be loaded.');
    } finally {
      setFileAdding(null);
    }
  };

  const remove = (sourceId: string) => {
    const source = config.dataSources.find((entry) => entry.id === sourceId);
    if (source) invalidateFields(source);
    // Detaches every widget that referenced it, so no widget is left pointing
    // at a source that no longer exists.
    onChange(removeDataSource(config, sourceId));
  };

  const rename = (sourceId: string, name: string) =>
    onChange({
      ...config,
      dataSources: config.dataSources.map((source) =>
        source.id === sourceId ? { ...source, name } : source,
      ),
    });

  const usageCount = (sourceId: string) =>
    config.widgets.filter(
      (widget) =>
        widget.dataSourceId === sourceId || widget.config.mapDataSourceIds?.includes(sourceId),
    ).length;

  return (
    <div className="space-y-6">
      {/* ── From an existing map application ───────────────────────────────
          Offered FIRST because it is the shorter road: a map app has already
          had its service URL entered and its layers picked, and retyping both
          to chart what that map already draws is work this can do instead. */}
      <section className="gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Use layers from a map app</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Pick one of your map applications and take its layers straight into this dashboard.
        </p>

        {apps.length === 0 ? (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-500">
            You have no map applications yet. Build one, or connect to a service below.
          </p>
        ) : (
          <>
            <select
              value={appId}
              onChange={(event) => {
                setAppId(event.target.value);
                setAppPicked(new Set());
              }}
              className="gl-input mt-3 w-full max-w-sm px-3 py-2 text-sm"
            >
              <option value="">Choose a map application…</option>
              {apps.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.title || entry.name} · {entry.layers?.length ?? 0} layers
                </option>
              ))}
            </select>

            {chosenApp && appLayers.length === 0 && (
              <p className="mt-3 rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-500">
                That map has no layers yet.
              </p>
            )}

            {appLayers.length > 0 && (
              <>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Layers ({usableAppLayers.length} usable of {appLayers.length})
                  </span>
                  {usableAppLayers.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setAppPicked((current) =>
                          current.size > 0
                            ? new Set()
                            : new Set(usableAppLayers.map((entry) => entry.layerId)),
                        )
                      }
                      className="text-xs font-semibold text-blue-600 transition hover:text-blue-800"
                    >
                      {appPicked.size > 0 ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>

                <div className="mt-1.5 max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                  {appLayers.map((candidate) => {
                    const Icon = GEOMETRY_ICONS[candidate.geometryType] ?? Minus;
                    const already = !candidate.unusable && sourceExists(config, candidate);
                    const blocked = Boolean(candidate.unusable) || already;
                    const checked = appPicked.has(candidate.layerId);
                    return (
                      <label
                        key={candidate.layerId}
                        className={`flex items-start gap-2 rounded px-1.5 py-1.5 ${
                          blocked ? 'opacity-60' : 'cursor-pointer hover:bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={blocked}
                          onChange={() =>
                            setAppPicked((current) => {
                              const next = new Set(current);
                              if (checked) next.delete(candidate.layerId);
                              else next.add(candidate.layerId);
                              return next;
                            })
                          }
                          className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
                        />
                        <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-slate-700">
                            {candidate.name}
                          </span>
                          {/* Says WHY a layer cannot be used, rather than
                              hiding it and leaving someone to wonder where a
                              layer they can see on the map went. */}
                          {(candidate.unusable || already) && (
                            <span className="block text-[11px] text-slate-400">
                              {already ? 'Already a source on this dashboard.' : candidate.unusable}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={addFromApp}
                  disabled={appPicked.size === 0}
                  className="gl-btn gl-btn--primary mt-3 px-3.5 py-2 text-sm"
                >
                  Add {appPicked.size > 0 ? appPicked.size : ''} layer
                  {appPicked.size === 1 ? '' : 's'}
                </button>
              </>
            )}
          </>
        )}
      </section>

      {/* ── From this computer ───────────────────────────────────────────
          Second because it is the direct road: no service to stand up, no map
          app to build first. The file's features are copied INTO the
          dashboard, which is what lets every widget query them. */}
      <section className="gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Or add data from this computer</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Import a file and chart it directly. Its records are stored inside this dashboard, so
          they keep working for everyone who opens it.
        </p>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            setFileDragging(true);
          }}
          onDragLeave={() => setFileDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setFileDragging(false);
            void handleLocalFile(event.dataTransfer.files?.[0]);
          }}
          className={`mt-3 rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
            fileDragging ? 'border-blue-400 bg-blue-50/60' : 'border-slate-300'
          }`}
        >
          <FileUp className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-700">
            Drop a file here, or
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="ml-1 font-semibold text-blue-600 underline-offset-2 hover:underline"
            >
              browse
            </button>
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            GeoJSON · KML · KMZ · Shapefile (.zip) · CSV/TSV with lon &amp; lat columns
            <br />up to {MAX_LOCAL_FILE_BYTES / 1024 / 1024} MB
          </p>
          <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
            <strong className="font-semibold text-slate-600">File Geodatabase · GeoPackage</strong>{' '}
            — zip the <code className="text-slate-600">.gdb</code> folder and add the .zip, up to{' '}
            {MAX_GDB_FILE_BYTES / 1024 / 1024} MB. Each feature class becomes its own source.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".geojson,.json,.kml,.kmz,.zip,.shp,.csv,.tsv,.txt"
            className="hidden"
            onChange={(event) => void handleLocalFile(event.target.files?.[0])}
          />
        </div>

        {fileParsing && (
          <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {fileParsingLabel}
          </p>
        )}

        {fileError && (
          <div role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              Could not import this file
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-red-600">{fileError}</p>
          </div>
        )}

        {fileImport && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {fileImport.layers.length === 1
                  ? '1 layer found'
                  : `${fileImport.layers.length} layers found`}
              </span>
              {fileImport.layers.length > 1 &&
                (() => {
                  const usable = fileImport.layers.flatMap((layer, index) =>
                    layer.unavailable ? [] : [index],
                  );
                  const allPicked = usable.length > 0 && filePicked.size === usable.length;
                  return (
                    <button
                      type="button"
                      onClick={() => setFilePicked(allPicked ? new Set() : new Set(usable))}
                      className="text-xs font-semibold text-blue-600 transition hover:text-blue-800"
                    >
                      {allPicked ? 'Clear all' : `Select all (${usable.length})`}
                    </button>
                  );
                })()}
            </div>

            {fileImport.notices.map((notice) => (
              <p key={notice} className="text-[11px] text-amber-700">• {notice}</p>
            ))}

            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
              {fileImport.layers.map((layer, index) => {
                const Icon = GEOMETRY_ICONS[layer.geometryType] ?? Minus;
                const blocked = Boolean(layer.unavailable);
                return (
                  <label
                    key={`${layer.name}-${index}`}
                    className={`flex items-start gap-2 rounded px-1.5 py-1.5 ${
                      blocked ? 'opacity-60' : 'cursor-pointer hover:bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={filePicked.has(index)}
                      disabled={blocked}
                      onChange={(event) =>
                        setFilePicked((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(index);
                          else next.delete(index);
                          return next;
                        })
                      }
                      className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-blue-600"
                    />
                    <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-slate-800">
                        {layer.name}
                      </span>
                      <span className="block text-[11px] text-slate-400">
                        {layer.featureCount.toLocaleString()} record
                        {layer.featureCount === 1 ? '' : 's'} · {layer.fields.length} field
                        {layer.fields.length === 1 ? '' : 's'}
                      </span>
                      {layer.unavailable && (
                        <span className="block text-[11px] font-medium text-slate-500">
                          {layer.unavailable}
                        </span>
                      )}
                      {layer.notices.map((notice) => (
                        <span key={notice} className="block text-[11px] text-amber-700">
                          {notice}
                        </span>
                      ))}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={filePicked.size === 0 || fileAdding !== null}
                onClick={() => void addFromFile()}
                className="gl-btn gl-btn--primary px-3 py-1.5 text-xs"
              >
                {fileAdding && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {fileAdding
                  ? `Loading ${Math.min(fileAdding.done + 1, fileAdding.total)} of ${fileAdding.total}…`
                  : filePicked.size <= 1
                    ? 'Add as data source'
                    : `Add ${filePicked.size} data sources`}
              </button>
              <button
                type="button"
                disabled={fileAdding !== null}
                onClick={() => {
                  fileImport.release?.();
                  resetFileImport();
                }}
                className="gl-btn gl-btn--secondary px-3 py-1.5 text-xs"
              >
                Choose another
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Connect ─────────────────────────────────────────────────────── */}
      <section className="gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Or connect to a service</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Connect to a GIS service and choose the layers this dashboard should read.
        </p>

        <div className="mt-3 grid max-w-xs grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
          {(['geoserver', 'arcgis'] as DashboardServiceType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setServiceType(type);
                setAvailable([]);
                setError(null);
                setConnectionBrowse(null);
                setConnectionBrowseError(null);
                setSelectedConnectionId(null);
              }}
              className={`rounded-md px-2 py-1.5 text-xs font-medium transition ${
                serviceType === type
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {type === 'geoserver' ? 'GeoServer' : 'ArcGIS REST'}
            </button>
          ))}
        </div>

        {/* Manual URL vs a connection saved in Settings › ArcGIS Servers /
            GeoServer — mirrors the Map App builder's identical toggle. */}
        <div className="mt-3 flex gap-1 text-xs">
          {([
            { id: 'manual' as const, label: 'Manual URL' },
            { id: 'connection' as const, label: 'Saved connection' },
          ]).map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setUrlSource(entry.id)}
              className={`rounded-full px-2.5 py-1 font-medium transition ${
                urlSource === entry.id ? 'bg-blue-50 text-blue-700' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {urlSource === 'manual' ? (
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void connect();
              }}
              placeholder={
                serviceType === 'geoserver'
                  ? 'https://host/geoserver/workspace/wms'
                  : 'https://host/arcgis/rest/services/<service>/FeatureServer'
              }
              aria-label={serviceType === 'geoserver' ? 'GeoServer WMS URL' : 'ArcGIS REST service URL'}
              className="gl-input min-w-0 flex-1 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void connect()}
              disabled={connecting}
              className="gl-btn gl-btn--primary px-4 py-2 text-sm"
            >
              {connecting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {connecting ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {connectionsLoading ? (
              <p className="text-xs text-slate-400">Loading saved connections…</p>
            ) : connections.filter((c) => c.type === serviceType).length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                No saved {serviceType === 'geoserver' ? 'GeoServer' : 'ArcGIS Server'} connections yet.
                Add one in Settings › {serviceType === 'geoserver' ? 'GeoServer' : 'ArcGIS Servers'}.
              </p>
            ) : !connectionBrowse ? (
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  value={selectedConnectionId ?? ''}
                  onChange={(event) => setSelectedConnectionId(event.target.value || null)}
                  aria-label="Saved connection"
                  className="gl-input min-w-0 flex-1 px-3 py-2 text-sm"
                >
                  <option value="">Choose a connection…</option>
                  {connections
                    .filter((c) => c.type === serviceType)
                    .map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name} — {connection.baseUrl}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => void connectSelectedConnection()}
                  disabled={!selectedConnectionId || connecting}
                  className="gl-btn gl-btn--primary px-4 py-2 text-sm"
                >
                  {connecting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {connecting
                    ? 'Connecting…'
                    : serviceType === 'geoserver'
                      ? 'Connect & load layers'
                      : 'Connect'}
                </button>
              </div>
            ) : (
              /* ArcGIS only: browsing this connection's services — see
                 AddLayersDialog's identical folder/service picker. */
              <div className="rounded-lg border border-slate-200">
                <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-2 text-[11px] text-slate-500">
                  <button
                    type="button"
                    onClick={() => void browseConnection('')}
                    className="font-medium text-slate-600 hover:text-blue-600"
                  >
                    Root
                  </button>
                  {connectionBrowse.path
                    .split('/')
                    .filter(Boolean)
                    .map((segment, index, segments) => (
                      <span key={index} className="flex items-center gap-1">
                        <ChevronRight className="h-3 w-3 text-slate-300" />
                        <button
                          type="button"
                          onClick={() => void browseConnection(segments.slice(0, index + 1).join('/'))}
                          className="font-medium text-slate-600 hover:text-blue-600"
                        >
                          {segment}
                        </button>
                      </span>
                    ))}
                  <button
                    type="button"
                    onClick={() => setSelectedConnectionId(null)}
                    className="ml-auto flex-shrink-0 text-slate-400 hover:text-slate-600"
                  >
                    Change connection
                  </button>
                </div>

                {connectionBrowseLoading ? (
                  <p className="px-3 py-6 text-center text-xs text-slate-400">Loading…</p>
                ) : connectionBrowse.folders.length === 0 && connectionBrowse.services.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-slate-400">Nothing here.</p>
                ) : (
                  <ul className="max-h-56 overflow-y-auto">
                    {connectionBrowse.folders.map((folder) => (
                      <li key={folder} className="border-b border-slate-50 last:border-0">
                        <button
                          type="button"
                          onClick={() =>
                            void browseConnection(
                              connectionBrowse.path ? `${connectionBrowse.path}/${folder}` : folder,
                            )
                          }
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                        >
                          <Folder className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate">{folder}</span>
                        </button>
                      </li>
                    ))}
                    {connectionBrowse.services.map((service) => (
                      <li key={`${service.name}-${service.type}`} className="border-b border-slate-50 last:border-0">
                        <button
                          type="button"
                          onClick={() => void selectConnectionService(service)}
                          disabled={connecting}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <LayersIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-400" />
                            <span className="min-w-0 truncate">{service.name || 'This service'}</span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                            {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : service.type}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {connectionBrowseError && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                {connectionBrowseError}
              </p>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        {available.length > 0 && (
          <div className="mt-4 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Available layers ({available.length})
              </span>
              <button
                type="button"
                onClick={addSelected}
                disabled={selected.size === 0}
                className="gl-btn gl-btn--accent px-3 py-1.5 text-xs"
              >
                Add {selected.size > 0 ? `(${selected.size})` : ''}
              </button>
            </div>
            <ul className="max-h-64 overflow-y-auto">
              {available.map((layer) => (
                <li key={layer.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 border-b border-slate-50 px-3 py-2 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selected.has(layer.id)}
                      onChange={() => toggle(layer.id)}
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                      {layer.title}
                    </span>
                    {layer.geometryType !== 'unknown' && (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                        {layer.geometryType}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── Added layers ────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-800">
          Dashboard layers ({config.dataSources.length})
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Widgets pick their data from this list — no need to re-enter service URLs.
        </p>

        {config.dataSources.length === 0 ? (
          <div className="mt-3 gl-panel border-dashed py-10 text-center">
            <Database className="mx-auto h-6 w-6 text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No layers added yet</p>
            <p className="text-xs text-slate-400">
              Take them from a map app, add a file, or connect to a service above.
            </p>
          </div>
        ) : (
          <ul className="mt-3 space-y-2">
            {config.dataSources.map((source) => {
              const Icon = GEOMETRY_ICONS[source.geometryType] ?? Minus;
              const used = usageCount(source.id);
              return (
                <li
                  key={source.id}
                  className="flex items-center gap-3 gl-panel px-3 py-2.5"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <input
                      value={source.name}
                      onChange={(event) => rename(source.id, event.target.value)}
                      aria-label={`Name for ${source.layerName}`}
                      className="w-full truncate rounded border border-transparent bg-transparent px-1 py-0.5 text-sm font-medium text-slate-800 outline-none hover:border-slate-200 focus:border-blue-300 focus:bg-white"
                    />
                    <p
                      className="truncate px-1 text-[11px] text-slate-400"
                      title={source.serviceUrl || source.originFile}
                    >
                      {describeDataSource(source)} · {source.layerName}
                      {/* An imported source has no URL to show, so the size of
                          what was imported is the useful fact instead. */}
                      {source.serviceType === 'local' &&
                        ` · ${(source.localData?.features.length ?? 0).toLocaleString()} records`}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-[10px] text-slate-400">
                    {used === 0 ? 'unused' : `${used} widget${used === 1 ? '' : 's'}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(source.id)}
                    aria-label={`Remove ${source.name}`}
                    title="Remove layer"
                    className="flex-shrink-0 rounded p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
